import isObject from "../../utility/isObject";
import OrderProduct from "../orderProduct";
import * as moment from 'moment';
import Store from "../store";
import User from "../user";
import Card from "../card";

class Order {
  public static fragmentsFields(): string {
    return `
      id
      shipName
      phonetic
      phone
      email
      shipMethod
      shipDate
      shipZipCode
      region
      city
      shipAddress1
      shipAddress2
      emergencyPhone
      receiptName
      totalProductPrice
      totalTaxPrice
      freight
      minPrice
      note
      totalPrice
      payment
      productOrders {
        count
        productId
        price
        taxPrice
      }
      card {
        ${Card.fragmentsFields()}
      }
    `;
  }

  public static fromJson(value: any): Order {
    if (!isObject(value)) {
      throw new TypeError('value must be an object for Customer.fromJson(value).');
    }

    const id = value['id'];
    const shipName = value['shipName'];
    const phonetic = value['phonetic'];
    const phone = value['phone'];
    const email = value['email'];
    const __shipDate = value['shipDate'];
    const shipZipCode = value['shipZipCode'];
    const region = value['region'];
    const city = value['city'];
    const shipAddress1 = value['shipAddress1'];
    const shipAddress2 = value['shipAddress2'];
    const emergencyPhone = value['emergencyPhone'];
    const receiptName = value['receiptName'];
    const totalProductPrice = value['totalProductPrice'];
    const totalTaxPrice = value['totalTaxPrice'];
    const note = value['note'];
    const totalPrice = value['totalPrice'];
    const freight = value['freight'];
    const minPrice = value['minPrice'];
    const shipMethod = value['shipMethod'];
    const payment = value['payment'];
    const card = value['card'] && Card.fromJson(value['card']);
    const shipDate = __shipDate && moment(new Date(__shipDate));

    return new Order({
      id,
      shipName,
      phonetic,
      phone,
      email,
      shipDate,
      shipZipCode,
      region,
      city,
      shipAddress1,
      shipAddress2,
      emergencyPhone,
      receiptName,
      totalProductPrice,
      totalTaxPrice,
      note,
      totalPrice,
      freight,
      minPrice,
      shipMethod,
      payment,
      card,
    });
  }

  public static fromSingedInUserParams(user: User): Partial<Order> {
    return {
      shipName: user.name,
      phonetic: user.phonetic,
      phone: user.phone,
      email: user.email,
      region: user.region,
      city: user.city,
      shipAddress1: user.address1,
      shipAddress2: user.address2,
    }
  }

  public id: string;
  public shipName: string;
  public phonetic: string;
  public phone: string;
  public email: string;
  public shipDate: moment.Moment | null;
  public shipZipCode: string;
  public region: string;
  public city: string;
  public shipAddress1: string;
  public shipAddress2: string;
  public emergencyPhone: string;
  public receiptName: string;
  public totalProductCount: number;
  public totalProductPrice: number;
  public totalTaxPrice: number;
  public note: string;
  public totalPrice: number;
  public freight: number;
  public minPrice: number;
  public shipMethod: string;
  public payment: string;
  public card: Card;

  constructor({
    id = '',
    shipName = '',
    phonetic = '',
    phone = '',
    email = '',
    shipDate = null,
    shipZipCode = '',
    region = '',
    city = '',
    shipAddress1 = '',
    shipAddress2 = '',
    emergencyPhone = '',
    receiptName = '',
    totalProductCount = 0,
    totalProductPrice = 0,
    totalTaxPrice = 0,
    note = '',
    totalPrice = 0,
    freight = 0,
    minPrice = 0,
    shipMethod = '',
    payment = 'cash',
    card = new Card({}),
  }: {
    id?: string;
    shipName?: string;
    phonetic?: string;
    phone?: string;
    email?: string;
    shipDate?: moment.Moment | null;
    shipZipCode?: string;
    region?: string;
    city?: string;
    shipAddress1?: string;
    shipAddress2?: string;
    emergencyPhone?: string;
    receiptName?: string;
    totalProductCount?: number;
    totalProductPrice?: number;
    totalTaxPrice?: number;
    note?: string;
    totalPrice?: number;
    freight?: number;
    minPrice?: number;
    shipMethod?: string;
    payment?: string;
    card?: Card;
  }) {
    this.id = id;
    this.shipName = shipName;
    this.phonetic = phonetic;
    this.phone = phone;
    this.email = email;
    this.shipDate = shipDate;
    this.shipZipCode = shipZipCode;
    this.region = region;
    this.city = city;
    this.shipAddress1 = shipAddress1;
    this.shipAddress2 = shipAddress2;
    this.emergencyPhone = emergencyPhone;
    this.receiptName = receiptName;
    this.totalProductCount = totalProductCount;
    this.totalProductPrice = totalProductPrice;
    this.totalTaxPrice = totalTaxPrice;
    this.note = note;
    this.totalPrice = totalPrice;
    this.freight = freight;
    this.minPrice = minPrice;
    this.shipMethod = shipMethod;
    this.payment = payment;
    this.card = card;
  }

  public toJson(orderProducts?: OrderProduct[]): object {
    const updateOrderProducts = orderProducts && orderProducts.map((orderProduct: OrderProduct) => {
      return orderProduct.toJson();
    });
    return {
      shipName: this.shipName,
      phonetic: this.phonetic,
      phone: this.phone,
      email: this.email || null,
      shipDate: this.shipDate || null,
      region: this.region,
      city: this.city,
      shipAddress1: this.shipAddress1,
      shipAddress2: this.shipAddress2,
      emergencyPhone: this.emergencyPhone,
      receiptName: this.receiptName,
      note: this.note,
      shipMethod: this.shipMethod || null,
      payment: this.payment,
      card: this.card && this.card.toJson(),
      orderProducts: updateOrderProducts,
    }
  }

  public setMinOrderDate(store: Store) {
    const minDate = store.minOrderDate();
    if (!this.shipDate || this.shipDate.isBefore(minDate)) {
      this.shipDate = minDate;
    }
  }

  public minPriceValid(): boolean {
    if (this.shipMethod === 'shop' || this.minPrice === 0) {
      return true;
    }
    if (this.minPrice === null) {
      return false;
    }
    return !(this.shipMethod === 'delivery' && (!this.minPrice || this.totalPrice < this.minPrice));
  }

  public shipAreaValid(): boolean {
    if (this.shipMethod === 'shop' || this.minPrice === 0) {
      return true;
    }
    return !!this.minPrice;
  }
}

export default Order;
